import React, { useState, useEffect } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { Editor } from "@tinymce/tinymce-react";

const AddExercisesModal = ({ isOpen, onClose, scenarioId }) => {

    // ⭐ ADDED: List CV formats
    const CV_FORMATS = [
        { key: "chronological", label: "Chronological" },
        { key: "skill-based", label: "Skill-Based" },
        { key: "functional", label: "Functional" },
        { key: "hybrid", label: "Hybrid" },
    ];

    const [activeTab, setActiveTab] = useState("add");
    const [activeTypeTab, setActiveTypeTab] = useState("cv");
    const [exercises, setExercises] = useState([]);
    const [selectedExerciseId, setSelectedExerciseId] = useState(null);
    const [selectedAttachIds, setSelectedAttachIds] = useState([]);
    const [hasUnselectedExisting, setHasUnselectedExisting] = useState(false);
    const [attachedOriginalIds, setAttachedOriginalIds] = useState([]);

    const [loading, setLoading] = useState(false);

    // ⭐ ADDED: format initialized
    const [form, setForm] = useState({
        title: "",
        level: "",
        estimated_time: "",
        instruction: "",
        input_placeholder: "",
        rubric: "",
        note: "",
        sample_answer: "",
        tips: "",
        format: "",      // ⭐ ADDED
    });

    // 🧱 CV form builder
    const [cvStructure, setCvStructure] = useState([
        {
            sectionTitle: "Personal Details",
            fields: [
                { label: "Full Name", type: "text" },
                { label: "Email", type: "email" },
                { label: "Phone", type: "text" },
            ],
        },
    ]);

    const fetchExercises = async () => {
        try {
            setLoading(true);
            const res = await axios.get("/writing_exercises/");
            setExercises(res.data || []);
        } catch (error) {
            console.error("Error fetching exercises:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAttachedExercises = async () => {
        try {
            const res = await axios.get(`/writing_exercises/by_scenario/${scenarioId}`);
            const attachedIds = res.data.map(ex => ex.id);
            setSelectedAttachIds(attachedIds);
            setAttachedOriginalIds(attachedIds);
            setHasUnselectedExisting(false);
        } catch (error) {
            console.error("Error fetching attached exercises:", error);
        }
    };

    useEffect(() => {
        if (!isOpen) return;

        // luôn load danh sách bài tập cho cả 3 tab
        fetchExercises();

        // chỉ tab attach mới cần fetch thêm attached list
        if (activeTab === "attach") {
            fetchAttachedExercises();
        }
    }, [isOpen, activeTab]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditorChange = (content) => {
        setForm((prev) => ({ ...prev, instruction: content }));
    };

    const handleSelectExercise = (e) => {
        const id = e.target.value;
        setSelectedExerciseId(id);
        const ex = exercises.find((ex) => ex.id === Number(id));
        if (ex) {
            setForm({
                title: ex.title || "",
                level: ex.level || "",
                estimated_time: ex.estimated_time_minutes || "",
                instruction: ex.instruction || "",
                input_placeholder: ex.input_placeholder || "",
                rubric: ex.rubric ? JSON.stringify(ex.rubric, null, 2) : "",
                note: ex.note || "",
                sample_answer: ex.sample_answer || "",
                tips: ex.tips || "",
                format: ex.format || "",      // ⭐ ADDED
            });
            setCvStructure(ex.cv_structure || []);
            setActiveTypeTab(ex.type || "essay");
        }
    };

    // 🧠 CV STRUCTURE BUILDER (giữ nguyên)
    const addSection = () =>
        setCvStructure((prev) => [
            ...prev,
            { sectionTitle: `Section ${prev.length + 1}`, fields: [] },
        ]);

    const removeSection = (index) =>
        setCvStructure((prev) => prev.filter((_, i) => i !== index));

    const addField = (sectionIndex) => {
        const updated = [...cvStructure];
        updated[sectionIndex].fields.push({ label: "New Field", type: "text" });
        setCvStructure(updated);
    };

    const removeField = (sectionIndex, fieldIndex) => {
        const updated = [...cvStructure];
        updated[sectionIndex].fields.splice(fieldIndex, 1);
        setCvStructure(updated);
    };

    const updateField = (sectionIndex, fieldIndex, key, value) => {
        const updated = [...cvStructure];
        updated[sectionIndex].fields[fieldIndex][key] = value;
        setCvStructure(updated);
    };

    // 🧠 SUBMIT TEMPLATE
    const handleSubmit = async () => {
        try {
            const payload = {
                title: form.title,
                type: activeTypeTab,
                format: activeTypeTab === "cv" ? form.format : null,   // ⭐ ADDED
                level: form.level,
                estimated_time_minutes: form.estimated_time,
                instruction: form.instruction,
                input_placeholder: form.input_placeholder,
                rubric: form.rubric ? JSON.parse(form.rubric) : null,
                note: form.note,
                sample_answer: form.sample_answer,
                tips: form.tips,
                cv_structure: activeTypeTab === "cv" ? cvStructure : null,
            };

            if (activeTab === "addToScenario") {
                payload.scenario_id = scenarioId;
            }

            if (activeTab === "add") {
                await axios.post("/writing_exercises/", payload);
                alert("✅ Exercise template created successfully!");
            }
            else if (activeTab === "edit" && selectedExerciseId) {
                await axios.put(`/writing_exercises/${selectedExerciseId}`, payload);
                alert("✅ Exercise template updated successfully!");
            }
            else if (activeTab === "addToScenario") {
                await axios.post("/writing_exercises/attach_to_scenario", payload);
                alert("✅ Exercise added to scenario successfully!");
            }

            onClose();
        } catch (error) {
            console.error("❌ Error saving exercise:", error);
        }
    };

    // 🧠 ATTACH / DETACH
    const toggleAttachSelect = (id) => {
        setSelectedAttachIds((prev) => {
            let newList;
            if (prev.includes(id)) {
                newList = prev.filter((x) => x !== id);
            } else {
                newList = [...prev, id];
            }

            const unselectedExisting = attachedOriginalIds.some(
                (oldId) => !newList.includes(oldId)
            );
            setHasUnselectedExisting(unselectedExisting);

            return newList;
        });
    };

    const handleAttach = async () => {
        try {
            await axios.post("/writing_exercises/attach_to_scenario", {
                scenario_id: scenarioId,
                exercise_ids: selectedAttachIds,
            });
            alert("✅ Updated exercises successfully!");
            await fetchAttachedExercises();
            await fetchExercises();
        } catch (error) {
            alert("❌ Failed to update exercises");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-6xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">

                {/* HEADER */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="text-lg font-semibold">
                        {activeTab === "add"
                            ? "Add Writing Exercise Template"
                            : activeTab === "edit"
                                ? "Edit Template"
                                : "Add Exercises to Scenario"}
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* TABS */}
                <div className="flex border-b">
                    {["add", "edit", "attach"].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-2 font-medium ${activeTab === tab
                                    ? "border-b-2 border-blue-600 text-blue-600"
                                    : "text-gray-500"
                                }`}
                        >
                            {tab === "add"
                                ? "Add New Template"
                                : tab === "edit"
                                    ? "Edit Existing"
                                    : "Add to Scenario"}
                        </button>
                    ))}
                </div>

                {/* CONTENT */}
                <div className="p-6 overflow-y-auto flex-1">

                    {/* --- TAB ATTACH UI --- */}
                    {activeTab === "attach" && (
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-blue-700">
                                Select Exercises to Attach to Scenario
                            </h3>

                            {loading ? (
                                <p>Loading exercises...</p>
                            ) : (
                                <table className="w-full border rounded-lg">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="p-2 border">Select</th>
                                            <th className="p-2 border">Title</th>
                                            <th className="p-2 border">Type</th>
                                            <th className="p-2 border">Level</th>
                                            <th className="p-2 border">Format</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {exercises.map((ex) => (
                                            <tr key={ex.id} className="border-b">
                                                <td className="p-2 border text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedAttachIds.includes(ex.id)}
                                                        onChange={() => toggleAttachSelect(ex.id)}
                                                    />
                                                </td>
                                                <td className="p-2 border">{ex.title}</td>
                                                <td className="p-2 border uppercase">{ex.type}</td>
                                                <td className="p-2 border">{ex.level}</td>
                                                <td className="p-2 border">{ex.format || "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {/* --- FORM (ADD / EDIT ONLY) --- */}
                    {activeTab !== "attach" && (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-4">

                                {/* TITLE */}
                                <div>
                                    <label className="block font-medium text-gray-700 mb-1">
                                        Title
                                    </label>
                                    <input
                                        name="title"
                                        value={form.title}
                                        onChange={handleChange}
                                        className="w-full border p-2 rounded"
                                    />
                                </div>

                                {/* ⭐ FORMAT BUTTONS */}
                                {activeTypeTab === "cv" && (
                                    <div>
                                        <label className="block font-medium text-gray-700 mb-1">
                                            CV Format
                                        </label>

                                        <div className="flex gap-3">
                                            {CV_FORMATS.map((fmt) => (
                                                <button
                                                    key={fmt.key}
                                                    type="button"
                                                    onClick={() =>
                                                        setForm((prev) => ({
                                                            ...prev,
                                                            format: fmt.key,
                                                        }))
                                                    }
                                                    className={`px-4 py-2 rounded border font-medium
                                                        ${form.format === fmt.key
                                                            ? "bg-blue-600 text-white border-blue-600"
                                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                                        }`}
                                                >
                                                    {fmt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* LEVEL */}
                                <div>
                                    <label className="block font-medium text-gray-700 mb-1">
                                        Level
                                    </label>
                                    <select
                                        name="level"
                                        value={form.level}
                                        onChange={handleChange}
                                        className="w-full border p-2 rounded"
                                    >
                                        <option value="">Select level</option>
                                        {["A1", "A2", "B1", "B2", "C1", "C2"].map((lvl) => (
                                            <option key={lvl} value={lvl}>
                                                {lvl}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* TIME */}
                                <div>
                                    <label className="block font-medium text-gray-700 mb-1">
                                        Estimated Time (minutes)
                                    </label>
                                    <input
                                        name="estimated_time"
                                        value={form.estimated_time}
                                        onChange={handleChange}
                                        className="w-full border p-2 rounded"
                                    />
                                </div>
                            </div>

                            {/* INSTRUCTION EDITOR */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block font-medium text-gray-700 mb-1">
                                        Instruction
                                    </label>
                                    <Editor
                                        apiKey="a7bzgpdrc4fmam222696wcyaaybnyn3vmqk9wk86wamc80as"
                                        value={form.instruction}
                                        init={{
                                            height: 200,
                                            menubar: false,
                                            plugins: ["link", "lists", "wordcount"],
                                            toolbar:
                                                "bold italic underline | bullist numlist | link | undo redo",
                                        }}
                                        onEditorChange={handleEditorChange}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CV BUILDER */}
                    {activeTab !== "attach" && activeTypeTab === "cv" && (
                        <div className="mt-8 border-t pt-6">
                            <h3 className="text-lg font-semibold mb-4 text-blue-700">
                                🧱 CV Form Structure Builder
                            </h3>

                            {cvStructure.map((section, sIndex) => (
                                <div
                                    key={sIndex}
                                    className="border rounded-lg p-4 mb-4 bg-gray-50 shadow-sm"
                                >
                                    <div className="flex justify-between items-center mb-3">
                                        <input
                                            value={section.sectionTitle}
                                            onChange={(e) => {
                                                const updated = [...cvStructure];
                                                updated[sIndex].sectionTitle = e.target.value;
                                                setCvStructure(updated);
                                            }}
                                            className="font-medium border-b border-gray-400 bg-transparent w-3/4"
                                        />
                                        <button
                                            className="text-red-600 hover:text-red-800"
                                            onClick={() => removeSection(sIndex)}
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>

                                    {section.fields.map((field, fIndex) => (
                                        <div key={fIndex} className="flex gap-2 mb-2">
                                            <input
                                                type="text"
                                                value={field.label}
                                                onChange={(e) =>
                                                    updateField(sIndex, fIndex, "label", e.target.value)
                                                }
                                                placeholder="Field label"
                                                className="flex-1 border p-2 rounded"
                                            />

                                            <select
                                                value={field.type}
                                                onChange={(e) =>
                                                    updateField(sIndex, fIndex, "type", e.target.value)
                                                }
                                                className="border p-2 rounded w-32"
                                            >
                                                <option value="text">Text</option>
                                                <option value="email">Email</option>
                                                <option value="number">Number</option>
                                                <option value="textarea">Textarea</option>
                                                <option value="date">Date</option>
                                            </select>

                                            <button
                                                className="text-red-600 hover:text-red-800"
                                                onClick={() => removeField(sIndex, fIndex)}
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    ))}

                                    <button
                                        onClick={() => addField(sIndex)}
                                        className="mt-2 flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                    >
                                        <Plus size={16} /> Add Field
                                    </button>
                                </div>
                            ))}

                            <button
                                onClick={addSection}
                                className="mt-2 px-4 py-2 bg-blue-100 rounded text-blue-700 hover:bg-blue-200"
                            >
                                + Add Section
                            </button>
                        </div>
                    )}
                </div>

                {/* FOOTER ADD / EDIT */}
                {activeTab !== "attach" && (
                    <div className="p-4 border-t flex justify-end bg-gray-50">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg border border-gray-300 mr-3 hover:bg-gray-100"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                        >
                            💾 Save Template
                        </button>
                    </div>
                )}

                {/* FOOTER ATTACH */}
                {activeTab === "attach" && (
                    <div className="p-4 border-t flex justify-end bg-gray-50">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg border border-gray-300 mr-3 hover:bg-gray-100"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAttach}
                            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                        >
                            📌 Save Attachments
                        </button>
                    </div>
                )}

            </div>
        </div>
    );
};

export default AddExercisesModal;
